import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import PageTitle from '@/components/ui/page-title/pageTitle.vue';
import GridTable from '@/components/ui/grid-table/gridTable.vue';
import { GridTableOptions, GridTablePaginationParams } from '@/components/ui/grid-table/gridTable.ts';
import { getShowingDate } from '@/helpers/date.helper';
import { numberFormatter } from '@/helpers/string.helper';
import { PspInvoiceOrderListRequest } from '@/types/request/psp-invoice-order-request.types';
import { ListResponseType } from '@/types/response/list-response.type';
import { PspInvoiceOrderType } from '@/types/api/psp-invoice-order/psp-invoice-order.type';
import ActionsCellRenderer from '@/components/ui/grid-table/cell/actions-cell-renderer/actionsCellRenderer.vue';
import { ActionsCellRendererParams } from '@/components/ui/grid-table/cell/actions-cell-renderer/actionsCellRenderer';
import InfoDialogMixin from '@/mixins/info-dialog.mixin';
import CommentDialogMixin from '@/mixins/comment-dialog.mixin';
import {
  ENTITY_MERCH_DEPOSIT_ORDER,
  ENTITY_PSP_INVOICE_ORDER,
  ENTITY_RETURN_ORDER, ENTITY_TRANSACTION, ENTITY_WALLET_RETURN_ORDER,
  IEntity
} from '@/constants/entity.constants';
import {
  getPspInvoiceOrderStatus, PSP_INVOICE_ORDER_STATUS_FAILED,
  PSP_INVOICE_ORDER_STATUS_SUCCESS
} from '@/constants/psp-invoice-order-status.constants';
import ExportXlsxBtn from '@/components/ui/buttons/export-xlsx-btn/export-xlsx-btn.vue';
import XlsExportMixin from '@/mixins/xls-export.mixin';
import { BSpinner, BTooltip } from 'bootstrap-vue';
import CopyCellRenderer from '@/components/ui/grid-table/cell/copy-cell-renderer/copy-cell-renderer.vue';
import { MODAL_CONFIG } from '@/helpers/app.helper';
import LogsModal from '@/components/features/modals/logs-modal/logs-modal.vue';
import TransactionsGrid from '@/components/features/grids/transactions-grid/transactions-grid.vue';
import GridMixin from '@/mixins/grid.mixin';
import UpdatePspInvoiceOrderModal
  from '@/components/features/modals/update-psp-invoice-order-modal/update-psp-invoice-order-modal.vue';
import { IPageTitle } from '@/components/ui/page-title/page-title.interface';
import PioManualChangeStatusModal
  from '@/components/features/modals/pio-manual-change-status-modal/pio-manual-change-status-modal.vue';
import CopyBtn from '@/components/ui/buttons/copy-btn/copy-btn.vue';
import { BookType } from 'xlsx';
import { OrderTransactionTypeEnum } from '@/constants/order-transaction-type.constants';

const title = 'pspInvoiceOrders.title';
const DEFAULT_SORT = {
  sortBy: 'createdAt',
  sortDesc: true
};

@Component({
  name: 'PspInvoiceOrdersGrid',
  components: {
    PageTitle,
    GridTable,
    ActionsCellRenderer,
    ExportXlsxBtn,
    BSpinner,
    CopyCellRenderer,
    BTooltip,
    TransactionsGrid,
    MerchDepositOrdersGrid: () => import('@/components/features/grids/merch-deposit-orders-grid/merch-deposit-orders-grid.vue'),
    WalletReturnOrdersGrid: () => import('@/components/features/grids/wallet-return-orders-grid/wallet-return-orders-grid.vue'),
    ReturnOrdersGrid: () => import('@/components/features/grids/return-orders-grid/return-orders-grid.vue'),
    CopyBtn,
  },
})
export default class PspInvoiceOrdersGrid extends Mixins(
  XlsExportMixin,
  InfoDialogMixin,
  CommentDialogMixin,
  GridMixin
) {
  title = title;
  tableName = 'pspInvoiceOrders';
  tableOptions: GridTableOptions = {
    pagination: {
      callback: this.gridChangeCallback
    },
    sort: DEFAULT_SORT,
    rowClickable: true,
    selectable: true,
    hasDetails: true,
  };
  permissionEntity: IEntity = ENTITY_PSP_INVOICE_ORDER;
  selectedItems: PspInvoiceOrderType[] = [];
  multiMode = true; // When the initial value is false, the selectable mode doesn't work
  isShowGrid = true;

  @Prop({
    required: false,
    default: () => ({
      showTitle: false,
      isMainTitle: false,
      class: 'mt-4',
      showDataCount: false,
    })
  })
  titleOptions?: IPageTitle;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  toggled?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withTransactions?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withMdo?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withWro?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => false
  })
  withRo?: boolean;

  @Prop({
    type: Boolean,
    required: false,
    default: () => true
  })
  withExport?: boolean;

  @Prop({
    required: false,
    default: () => ({})
  })
  gridFilter?: PspInvoiceOrderListRequest;

  @Watch('multiMode')
  multiModeChange(newVal) {
    this.tableOptions.selectable = newVal;
    if (!newVal) {
      this.selectedItems = [];
    }
  }

  @Action('getPspInvoiceOrders', { namespace: 'pspInvoiceOrders' })
  getPspInvoiceOrders!: (params?: PspInvoiceOrderListRequest) => Promise<ListResponseType<PspInvoiceOrderType>>;

  @Getter('getPspInvoiceOrders', { namespace: 'pspInvoiceOrders' })
  exportData!: ListResponseType<PspInvoiceOrderType>;

  created() {
    this.filter = this.gridFilter as any;
    this.multiMode = false;

    if (this.toggled) {
      this.isShowGrid = false;
    }
  }

  gridChangeCallback(params: GridTablePaginationParams) {
    return this.getPspInvoiceOrders({
      ...this.paginationFilter(params),
      ...this.prepareFilter(),
    });
  }

  get tableFields() {
    const fields = this.withTransactions || this.withMdo || this.withWro || this.withRo
      ? [{
        label: '',
        key: 'details',
        thStyle: { width: '40px' },
      }]
      : [];

    return [
      ...fields,
      {
        label: this.$t('pspInvoiceOrders.externalId'),
        key: 'externalId',
      },
      {
        label: this.$t('pspInvoiceOrders.partnerExternalId'),
        key: 'partnerExternalId',
      },
      {
        label: this.$t('pspInvoiceOrders.status'),
        key: 'status',
        formatter: (value: string) => getPspInvoiceOrderStatus(value)
      },
      {
        label: this.$t('pspInvoiceOrders.amount'),
        key: 'amount',
        thClass: 'text-right',
        tdClass: 'text-right',
        formatter: (value: number) => numberFormatter(value),
      },
      {
        label: this.$t('pspInvoiceOrders.phoneNumber'),
        key: 'phoneNumber',
      },
      {
        label: this.$t('pspInvoiceOrders.telecomProviderId'),
        key: 'telecomProviderId',
      },
      {
        label: this.$t('pspInvoiceOrders.createdAt'),
        key: 'createdAt',
        sortable: true,
        formatter: (value: string) => getShowingDate(value)
      },
      {
        label: '',
        key: 'actions',
        thStyle: { width: '40px' },
        tdClass: ['action']
      },
    ];
  }

  getActionsParams(data): ActionsCellRendererParams<PspInvoiceOrderType> {
    return {
      data: data.item,
      buttons: [
        // {
        //   type: 'comment',
        //   actionCallback: (params: ActionsCellRendererParams<PspInvoiceOrderType>) => {
        //     this.showCommentDialog(params.data.id, ENTITY_PSP_INVOICE_ORDER)
        //   },
        //   btnContent: `<div class="comment-count ${data.item.commentsCount ? 'filled' : ''}">${data.item.commentsCount || 0}</div>`,
        //   show: () => this.canEntityCreate(ENTITY_COMMENTS)
        // },
        // {
        //   type: 'details',
        //   actionCallback: (params: ActionsCellRendererParams<PspInvoiceOrderType>) => {
        //     this.$router.push({
        //       name: 'details-card',
        //       query: {
        //         pspInvoiceOrderId: params.data.id.toString()
        //       }
        //     })
        //   },
        //   btnContent: '<div class="circle-icon">d</div>',
        // },
        // {
        //   type: 'logs',
        //   actionCallback: this.onLogsAction,
        //   btnContent: '<div class="circle-icon">h</div>',
        //   show: () => this.canEntityRead(ENTITY_PSP_INVOICE_ORDER_LOGS)
        // },
        // {
        //   type: 'singleEdit',
        //   actionCallback: this.onSingleEditAction,
        //   btnContent: '<div class="circle-icon">s</div>',
        // },
        {
          type: 'info',
          actionCallback: (params: ActionsCellRendererParams<PspInvoiceOrderType>) => {
            this.showInfoDialog(params.data, title);
          },
          btnContent: '<div class="circle-icon">i</div>',
        },
      ]
    };
  }

  onLogsAction(params: ActionsCellRendererParams<PspInvoiceOrderType>) {
    this.$modal.show(LogsModal, {
      title: this.$t(title),
      externalId: params.data.externalId,
      type: 'pspInvoiceOrder',
      recordId: params.data.id,
    }, MODAL_CONFIG);
  }

  onSingleEditAction(params: ActionsCellRendererParams<PspInvoiceOrderType>) {
    const notChangeIds = params.data.status === PSP_INVOICE_ORDER_STATUS_SUCCESS
      || params.data.status === PSP_INVOICE_ORDER_STATUS_FAILED
      ? [params.data.id] : [];
    const changeItems = params.data.status !== PSP_INVOICE_ORDER_STATUS_SUCCESS
      && params.data.status !== PSP_INVOICE_ORDER_STATUS_FAILED
      ? [params.data] : [];
    this.openManualChangeModal(notChangeIds, changeItems);
  }

  onExport(event, bookType: BookType = 'xlsx') {
    this.xlsExport({
      title,
      action: this.getPspInvoiceOrders,
      sort: DEFAULT_SORT,
      bookType,
      content: [
        {
          label: this.$t('pspInvoiceOrders.externalId'),
          getValue: (item: PspInvoiceOrderType) => item.externalId
        },
        {
          label: this.$t('pspInvoiceOrders.partnerExternalId'),
          getValue: (item: PspInvoiceOrderType) => item.partnerExternalId
        },
        {
          label: this.$t('pspInvoiceOrders.status'),
          getValue: (item: PspInvoiceOrderType) => getPspInvoiceOrderStatus(item.status)
        },
        {
          label: this.$t('pspInvoiceOrders.amount'),
          getValue: (item: PspInvoiceOrderType) => this.numberFormatter(item.amount)
        },
        {
          label: this.$t('pspInvoiceOrders.phoneNumber'),
          getValue: (item: PspInvoiceOrderType) => item.phoneNumber
        },
        {
          label: this.$t('pspInvoiceOrders.telecomProviderId'),
          getValue: (item: PspInvoiceOrderType) => item.telecomProviderId
        },
        {
          label: this.$t('pspInvoiceOrders.createdAt'),
          getValue: (item: PspInvoiceOrderType) => getShowingDate(item.createdAt)
        },
      ]
    })
  }

  get canReadTransactions() {
    return this.canEntityRead(ENTITY_TRANSACTION);
  }

  get canReadMDO() {
    return this.canEntityRead(ENTITY_MERCH_DEPOSIT_ORDER);
  }

  get canReadWRO() {
    return this.canEntityRead(ENTITY_WALLET_RETURN_ORDER);
  }

  get canReadRO() {
    return this.canEntityRead(ENTITY_RETURN_ORDER);
  }

  onAddNew() {
    this.$modal.show(UpdatePspInvoiceOrderModal, {
      createCallback: () => {
        this.refreshTable();
      }
    }, MODAL_CONFIG);
  }

  onRowSelected(items: PspInvoiceOrderType[]) {
    this.selectedItems = items;
  }

  getDataCount() {
    return this.gridData?.data?.length || 0;
  }

  selectAllRows() {
    this.table?.selectAllRows();
  }

  unselectAllRows() {
    this.table?.clearSelected();
  }

  multipleEdit() {
    const notChangeIds = this.selectedItems.filter(
      item => item.status === PSP_INVOICE_ORDER_STATUS_SUCCESS
        || item.status === PSP_INVOICE_ORDER_STATUS_FAILED
    )
      .map(item => item.id);
    const changeItems = this.selectedItems.filter(
      item => item.status !== PSP_INVOICE_ORDER_STATUS_SUCCESS
        && item.status !== PSP_INVOICE_ORDER_STATUS_FAILED
    );

    this.openManualChangeModal(notChangeIds, changeItems);
  }

  openManualChangeModal(notChangeIds: number[], changeItems: PspInvoiceOrderType[]) {
    if (notChangeIds.length) {
      this.$modal.show('dialog', {
        title: this.$t('pspInvoiceOrders.itemsStatusesNotChange', { ids: notChangeIds.join(', ')}),
        buttons: [
          {
            title: this.$t('common.cancel'),
            handler: () => {
              this.$modal.hide('dialog');
            }
          },
          {
            title: 'OK',
            handler: () => {
              this.$modal.hide('dialog');
              this.$modal.show(PioManualChangeStatusModal, {
                ids: changeItems.map(item => item.id),
                createCallback: () => {
                  this.refreshTable();
                }
              }, MODAL_CONFIG);
            }
          }
        ]
      });
    } else {
      this.$modal.show(PioManualChangeStatusModal, {
        ids: changeItems.map(item => item.id),
        createCallback: () => {
          this.selectedItems = [];
          this.table?.clearSelected();
          this.refreshTable();
        }
      }, MODAL_CONFIG);
    }
  }

  getUrl(action: string) {
    return `${window.location.origin}/${action}`
  }

  onClickTitle() {
    if (this.toggled) {
      this.isShowGrid = !this.isShowGrid;
    }
  }

  get transactionType() {
    return OrderTransactionTypeEnum.Pio
  }
}
